import {
  ConversationStatus,
  SearchableConversationFilterInput
} from 'generated-graphql/types';
import {
  ControlFilter,
  ConversationStatusFilter,
  ConversationFilterIds,
  MeetingFilter,
  ScreeningStatusFilter
} from 'modules/conversations/constants/enums';
import {
  schedulingIncompleteFilter,
  schedulingSatisfiedFilter
} from 'modules/conversations/constants/schedulingStatusFilters';
import { CampaignType } from 'services/campaign/types';
import { ApplicationStage } from '../types';
import { ConversationTableFilter } from '../types/ConversationTableFilter';
import { isColumnFilter, isTagFilter } from './tagFilterIds';
import { canonicalTag } from './canonicalTag';

export const getConversationFiltersFromFilterValue = (
  filters: ConversationTableFilter[],
  campaignType?: CampaignType | null
): SearchableConversationFilterInput => {
  if (!filters?.length) return {};

  const selectedTags = filters.filter(isTagFilter).map(({ tag }) => tag);
  const columnFilters = filters.filter(isColumnFilter);

  // Every tag in one OR, so selecting more tags widens the list. Matched on the
  // canonical form: a conversation stores the spelling its own file used.
  const tagClause = selectedTags.length
    ? [
        {
          or: selectedTags.map((tag) => ({
            tagsNormalized: { eq: canonicalTag(tag) }
          }))
        }
      ]
    : [];

  const filterResults = columnFilters.map(({ filterId, value }) => {
    switch (filterId) {
      case ConversationFilterIds.status: {
        if (value === ConversationStatusFilter.CLOSED) {
          return {
            or: [
              {
                status: {
                  eq: ConversationStatus.CompletedMeetingRequested
                }
              },
              {
                status: {
                  eq: ConversationStatus.CompletedAwaitingCall
                }
              },
              {
                status: {
                  eq: ConversationStatus.CompletedCustomRequest
                }
              },
              {
                status: {
                  eq: ConversationStatus.CompletedRejected
                }
              },
              {
                status: {
                  eq: ConversationStatus.Completed
                }
              },
              {
                status: {
                  eq: ConversationStatus.CompletedNotInterested
                }
              }
            ]
          };
        }

        if (value === ConversationStatusFilter.ONGOING) {
          return {
            status: {
              eq: ConversationStatus.Ongoing
            }
          };
        }

        if (value === ConversationStatusFilter.REVIEW) {
          return {
            needsReview: {
              eq: true
            }
          };
        }

        if (value === ConversationStatusFilter.MORE_TIME_REQUESTED) {
          return {
            unresolvedNeedsReviewReasons: {
              eq: 'MORE_TIME_REQUESTED'
            }
          };
        }

        if (value === ConversationStatusFilter.OPTED_OUT) {
          return {
            status: {
              eq: ConversationStatus.OptedOut
            }
          };
        }

        if (value === ConversationStatusFilter.CREATED) {
          return {
            status: {
              eq: ConversationStatus.Created
            }
          };
        }

        if (value === ConversationStatusFilter.NOT_DELIVERED) {
          return {
            status: {
              eq: ConversationStatus.DeliveryFailed
            }
          };
        }

        if (value === ConversationStatusFilter.QUEUED) {
          return {
            status: {
              eq: ConversationStatus.Queued
            }
          };
        }

        return {};
      }
      case ConversationFilterIds.screeningStatus: {
        if (campaignType === CampaignType.SCHEDULING) {
          if (value === ScreeningStatusFilter.PROGRESSED) {
            return schedulingSatisfiedFilter;
          }

          if (value === ScreeningStatusFilter.INCOMPLETE) {
            return schedulingIncompleteFilter;
          }
        }

        if (value === ScreeningStatusFilter.PROGRESSED) {
          return {
            or: [
              {
                status: {
                  eq: ConversationStatus.CompletedMeetingRequested
                }
              },
              {
                status: {
                  eq: ConversationStatus.CompletedAwaitingCall
                }
              },
              {
                status: {
                  eq: ConversationStatus.CompletedCustomRequest
                }
              }
            ]
          };
        }

        if (value === ScreeningStatusFilter.REJECTED) {
          return {
            status: {
              eq: ConversationStatus.CompletedRejected
            }
          };
        }

        if (value === ScreeningStatusFilter.NOT_INTERESTED) {
          return {
            status: {
              eq: ConversationStatus.CompletedNotInterested
            }
          };
        }

        if (value === ScreeningStatusFilter.INCOMPLETE) {
          return {
            status: {
              eq: ConversationStatus.Completed
            }
          };
        }

        if (value === ScreeningStatusFilter.OPTED_OUT) {
          return {
            status: {
              eq: ConversationStatus.OptedOut
            }
          };
        }

        return {};
      }
      case ConversationFilterIds.control: {
        if (value === ControlFilter.MANUAL) {
          return {
            isHumanTakeover: {
              eq: true
            }
          };
        }

        if (value === ControlFilter.AI) {
          return {
            isHumanTakeover: {
              eq: false
            }
          };
        }

        return {};
      }
      case ConversationFilterIds.applicationStage:
        if (value === ApplicationStage.NO_STAGE) {
          return {
            applicationStage: {
              exists: false
            }
          };
        }

        return {
          applicationStage: {
            eq: String(value)
          }
        };
      case ConversationFilterIds.meetingBookingStatus:
        if (value === MeetingFilter.NO_MEETING) {
          return {
            meetingBookingStatus: {
              exists: false
            }
          };
        }

        return {
          meetingBookingStatus: {
            eq: String(value)
          }
        };
      default:
        return {};
    }
  });

  return {
    and: [...filterResults, ...tagClause]
  };
};
